import React, { useState } from 'react';

const guides = {
  '⚡ 30-second quickstart': `# 1. One command — creates an app + writes your publishable key to .env:
npx @authaas/cli init --key sk_live_...   # --template react to scaffold too

# 2. Frontend — TWO lines. The publishable key is the ONLY config; branding,
#    enabled methods, and password rules are fetched automatically.
import { AuthProvider, SignInForm } from "@authaas/auth-components";

export default function App() {
  return (
    <AuthProvider publishableKey={import.meta.env.VITE_AUTHAAS_PUBLISHABLE_KEY}>
      <SignInForm onSuccess={() => (location.href = "/")} />
    </AuthProvider>
  );
}

# 3. Backend — ONE line. Verifies our tokens locally against the public JWKS
#    (no secret, no round-trip):
import { requireAuth } from "@authaas/node";
app.get("/api/me", requireAuth(), (req, res) => res.json(req.auth));`,
  'React (frontend)': `npm i @authaas/auth-components

# The publishable key is auto-attached to signup/login and the org config is
# fetched from it — no apiUrl, no clientId needed.
import { AuthProvider, SignInForm, SignUpForm, UserProfile } from "@authaas/auth-components";

<AuthProvider publishableKey="pk_live_...">
  <SignInForm onSuccess={() => location.assign("/")} />
  {/* also: <SignUpForm/>, <MagicLinkForm/>, <PasskeyButton/>, <UserProfile/> */}
</AuthProvider>`,
  'Next.js (route protection)': `npm i @authaas/node

// app/api/me/route.ts — App Router
import { withAuth } from "@authaas/node";
export const GET = withAuth(async (_req, claims) => Response.json(claims));

// or in middleware / a server action:
import { verifyAccessToken } from "@authaas/node";
const claims = await verifyAccessToken(token); // throws if invalid`,
  'Node / Express (backend)': `npm i @authaas/node

import express from "express";
import { requireAuth } from "@authaas/node";
const app = express();

// Drop-in: 401s missing/invalid tokens, sets req.auth = claims on success.
app.get("/api/profile", requireAuth(), (req, res) => res.json(req.auth));

// Enforce a scope or role:
app.post("/api/admin", requireAuth({ role: "admin" }), handler);`,
  'Python (backend)': `pip install "authaas[verify]"

from authaas import verify_token

# Validate an end-user token LOCALLY (no API round-trip):
claims = verify_token(bearer_token)   # raises on invalid/expired
user_id = claims["sub"]`,
  'Quick start (curl)': `# 1. Create an application + secret key in the Applications tab.
# 2. Call the API with your secret key:

curl https://api.dev.auth.isaacmccourt.dev/v1/users \\
  -H "Authorization: Bearer sk_live_..."

# Create a user (requires the users:write scope):
curl -X POST https://api.dev.auth.isaacmccourt.dev/v1/users \\
  -H "Authorization: Bearer sk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"email":"user@example.com"}'`,
  'Quick start (Node)': `const BASE = "https://api.dev.auth.isaacmccourt.dev/v1";
const KEY = process.env.AUTHAAS_SECRET_KEY; // sk_live_...

async function listUsers() {
  const res = await fetch(\`\${BASE}/users\`, {
    headers: { Authorization: \`Bearer \${KEY}\` },
  });
  return res.json();
}`,
  'Token validation': `// Validate an access token your end-users present to YOUR backend.
const res = await fetch(\`\${BASE}/tokens/validate\`, {
  method: "POST",
  headers: { Authorization: \`Bearer \${KEY}\`, "Content-Type": "application/json" },
  body: JSON.stringify({ token: userAccessToken }),
});
const { valid, claims } = await res.json();`,
  'Publishable vs secret keys': `# Publishable key (pk_live_...) — safe for client-side / your frontend.
#   Send it on signup/login so new users are attributed to YOUR organization
#   and your org's settings (password policy, allowed domains, MFA) are enforced:

curl -X POST https://api.dev.auth.isaacmccourt.dev/v1/auth/signup \\
  -H "Content-Type: application/json" \\
  -H "X-Authaas-Publishable-Key: pk_live_..." \\
  -d '{"email":"newuser@example.com","password":"..."}'

# Secret key (sk_live_...) — SERVER-SIDE ONLY. Never expose it to a browser.
#   Used for the management API above (list/create users, validate tokens, webhooks).`,
  'Webhooks': `# Register a webhook (Webhooks tab or API). You get a signing secret once.
# Verify each delivery's signature:

import crypto from "crypto";
function verify(rawBody, signatureHeader, secret) {
  const expected = "sha256=" + crypto.createHmac("sha256", secret).update(rawBody).digest("hex");
  return crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(signatureHeader));
}
// Header: X-Authaas-Signature: sha256=<hmac>  ·  X-Authaas-Event: user.created`,
};

export default function GuidesPage() {
  const titles = Object.keys(guides);
  const [active, setActive] = useState(titles[0]);
  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Guides</h1>
      <p className="text-gray-600 mb-6">Copy-paste integration recipes.</p>
      <div className="flex gap-2 flex-wrap mb-4">
        {titles.map((t) => (
          <button key={t} onClick={() => setActive(t)} className={`px-3 py-1.5 rounded-md text-sm ${active === t ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200'}`}>{t}</button>
        ))}
      </div>
      <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-sm overflow-auto whitespace-pre-wrap">{(guides as any)[active]}</pre>
    </div>
  );
}
